import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { updatePersonSettings } from 'services/people'

/**
 * Manages the "Display photo in Irwin app" setting for a person.
 *
 * The toggle state is seeded and kept in sync with the person's saved
 * `profileImage` setting, so saving without touching the toggle preserves the
 * stored value instead of forcing it back on. The setting defaults to enabled
 * and is only disabled when the BE explicitly stores `false`.
 *
 * @param {object} args - hook arguments
 * @param {string} args.personId - id of the person whose settings are edited
 * @param {boolean} [args.profileImage] - the person's saved `profileImage` setting
 * @returns {{
 *   isProfileImageEnabled: boolean,
 *   handleProfilePictureToggle: (isEnabled: boolean) => void,
 *   updatePersonSettingsMutation: import('@tanstack/react-query').UseMutationResult,
 * }}
 */
const usePersonSettings = ({ personId, profileImage }) => {
  // enabled by default, only disabled when the backend explicitly stored `false`.
  const isProfileImageEnabledSetting = profileImage !== false
  const [isProfileImageEnabled, setIsProfileImageEnabled] = useState(isProfileImageEnabledSetting)

  // Re-sync once the person's saved setting loads (or changes after a refetch).
  // User toggles are preserved because this only fires when the saved value changes.
  useEffect(() => {
    setIsProfileImageEnabled(isProfileImageEnabledSetting)
  }, [isProfileImageEnabledSetting])

  const updatePersonSettingsMutation = useMutation(() =>
    updatePersonSettings(personId, { profileImage: isProfileImageEnabled }),
  )

  return {
    isProfileImageEnabled,
    handleProfilePictureToggle: setIsProfileImageEnabled,
    updatePersonSettingsMutation,
  }
}

export default usePersonSettings
